import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { normalizeImageCrop } from '../content/imageCrop';
import type { ImageCrop, SiteContent, SiteSectionId } from '../content/types';
import { facebookPluginUrl, normalizeFacebookUrl, SUGARLOAF_FACEBOOK_PAGE_URL } from '../content/facebook';
import { postJson, type Litter } from '../lib/api';
import { useUiStore } from '../store/uiStore';
import { useDocumentTitle } from '../lib/useDocumentTitle';

// Photo pool used to fill the decorative image slots (film strip + gallery).
const photoPool = [
  '/images/bento_puppies.jpg',
  '/images/about.jpg',
  '/images/hero_mountain.jpg',
  '/images/lineage_portrait.jpg',
  '/images/hero_new.jpg',
  '/images/sleepy_golden.png',
  '/images/husky_puppy.png',
  '/images/grey_puppy.png',
];

const stripData = [
  { label: 'whelping box', w: 280, tilt: -1.2, dy: 0 },
  { label: 'first swim', w: 230, tilt: 0.8, dy: 8 },
  { label: 'mountain trail walk', w: 300, tilt: -0.6, dy: 0 },
  { label: 'gotcha day', w: 250, tilt: 1, dy: 6 },
  { label: 'puppy pile nap', w: 260, tilt: -0.8, dy: 4 },
  { label: 'porch mornings', w: 240, tilt: 0.6, dy: 10 },
];

const quizData = [
  { q: 'How active is your household?', options: [['Daily walks, hikes, or runs, we love being out', 3], ['Moderately active, walks most days', 2], ['Pretty quiet, mostly homebodies', 1]] as const },
  { q: 'How long would the dog be alone on a typical day?', options: [['Rarely more than a couple hours', 3], ['4–6 hours, with a midday break', 2], ['8+ hours most weekdays', 1]] as const },
  { q: 'Goldens shed. A lot. Year-round. How do you feel?', options: [['Fine, lint rollers are a lifestyle', 3], ['Manageable if we keep up with grooming', 2], ['Honestly, that might bother us', 1]] as const },
  { q: 'Vet care, food, and training run $2–3k a year. Ready?', options: [['Budgeted and ready', 3], ["We'd need to plan, but it's doable", 2], ["That's more than we expected", 1]] as const },
  { q: 'A golden puppy is a land shark for months. Your plan?', options: [["Training classes, crate routine, we've done this before", 3], ['First-timers, but committed to learning', 2], ["We're hoping it just works out", 1]] as const },
];

const processData = [
  ['1', 'Apply', "Ten minutes about your family, home, and what you're looking for in a golden. Drafts save automatically."],
  ['2', 'Meet us', 'A call, and a visit when possible, so you can meet our dogs and we can get to know each other.'],
  ['3', 'Get matched', 'At 7 weeks we temperament-test each puppy and match them to families, not the other way around.'],
  ['4', 'Gotcha day', 'Home at 8+ weeks with health records, a puppy pack, and our phone number for life.'],
];

const testimonialsData = [
  { quote: 'We visited three breeders before Sugarloaf. Seeing the dogs live in the house, the health clearances laid out on the table, it was a different level of care entirely.', name: 'Sarah & Mike', location: 'Arlington, VA' },
  { quote: "Our boy came home crate-conditioned and confident, our trainer couldn't believe he was 9 weeks old.", name: 'The Hendersons', location: 'Bethesda, MD' },
  { quote: 'Two years later and they still answer every question within a day. Worth every mile of the drive.', name: 'The Okafors', location: 'Philadelphia, PA' },
  { quote: 'Three years in and Bear still gets compliments on his manners daily. The temperament matching Sugarloaf did was spot on, exactly the calm, easygoing dog our family needed.', name: 'The Millers', location: 'Frederick, MD' },
  { quote: 'The weekly updates and photos meant we felt like part of the litter before we even brought her home.', name: 'Priya & Dev', location: 'Rockville, MD' },
  { quote: 'Health testing paperwork was ready before we even asked. That told us everything we needed to know.', name: 'The Caldwells', location: 'Leesburg, VA' },
];

const goHomeItems = ['Written health guarantee', 'Vet exam & records', 'Vaccinations & deworming', 'AKC registration', 'Familiar-scent toy', 'Starter food & guide', 'Lifetime breeder support'];

const healthIcons: Record<string, ReactNode> = {
  OFA: <path d="M4 12c-.5-1.5-.3-3 .8-4a2.3 2.3 0 0 0 .6-2.6A2 2 0 1 1 9 4a2.3 2.3 0 0 0 1.2 2.3c1.1.6 2.5.6 3.6 0A2.3 2.3 0 0 0 15 4a2 2 0 1 1 3.6 1.4A2.3 2.3 0 0 0 19.2 8c1.1 1 1.3 2.5.8 4-.5 1.5-.3 3-1.4 4a2 2 0 1 1-2.9 2.7A2.3 2.3 0 0 0 14 17.5a3.6 3.6 0 0 0-4 0 2.3 2.3 0 0 0-1.7 1.2A2 2 0 1 1 5.4 16c-1.1-1-.9-2.5-1.4-4Z" />,
  ECHO: <><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" /><path d="M3.5 12h4l1.5-3 2.5 6 1.5-3h5" /></>,
  CAER: <><path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z" /><circle cx="12" cy="12" r="3" /></>,
  DNA: <><path d="M6 4c0 4 12 4 12 8s-12 4-12 8" /><path d="M18 4c0 4-12 4-12 8s12 4 12 8" /><path d="M7.5 8h1.2M9.5 12h5M7.5 16h1.2M15.3 8h1.2M15.3 16h1.2" /></>,
};

// Resolve a health item's icon by its title, so items can display without a
// clearance-code badge (e.g. Cardiac, Eyes) and still show the right icon.
const healthIconFor = (item: { code: string; title: string }): ReactNode => {
  const title = item.title.toLowerCase();
  if (title.includes('hip') || title.includes('elbow')) return healthIcons.OFA;
  if (title.includes('cardiac') || title.includes('heart')) return healthIcons.ECHO;
  if (title.includes('eye')) return healthIcons.CAER;
  if (title.includes('genetic') || title.includes('dna')) return healthIcons.DNA;
  return healthIcons[item.code] ?? healthIcons.OFA;
};

type Props = { content: SiteContent; litters: Litter[] };

type CroppedImageProps = {
  src: string;
  alt: string;
  crop?: ImageCrop | null;
  fallbackPosition: string;
};

function cropImageStyle(crop: ImageCrop | null | undefined, fallbackPosition: string): CSSProperties {
  const normalized = normalizeImageCrop(crop);
  if (!normalized) return { objectPosition: fallbackPosition };
  const position = `${normalized.x}% ${normalized.y}%`;
  return {
    objectPosition: position,
    transform: `scale(${normalized.zoom})`,
    transformOrigin: position,
  };
}

function CroppedImage({ src, alt, crop, fallbackPosition }: CroppedImageProps) {
  if (!src) return null;
  return <img className="site-crop-image" src={src} alt={alt} style={cropImageStyle(crop, fallbackPosition)} />;
}

function Reveal({ delay = 0, className = '', children }: { delay?: number; className?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) { setShown(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) { setShown(true); io.unobserve(entry.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    io.observe(el);
    const safety = window.setTimeout(() => setShown(true), 4000);
    return () => { io.disconnect(); window.clearTimeout(safety); };
  }, []);
  return <div ref={ref} className={`reveal ${shown ? 'is-shown' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

export function HomePage({ content, litters }: Props) {
  useDocumentTitle('Sugarloaf Golden Retrievers | Urbana, Maryland');
  const { menuOpen, lineageId, toggleMenu, closeMenu, openLineage, closeOverlays } = useUiStore();
  const copy = content.copy;
  const weeksData = content.weeks;
  const hiddenSections = new Set(content.sections.hidden);
  const sectionState = (id: SiteSectionId): { hidden: boolean; style: CSSProperties } => {
    const index = content.sections.order.indexOf(id);
    return { hidden: hiddenSections.has(id), style: { order: index === -1 ? 99 : index } };
  };
  // Editable decorative photo pool (falls back to built-ins) driving the hero film-strip.
  const pool = content.heroPhotos?.length ? content.heroPhotos : photoPool;
  const photoSlot = (index: number) => ((index % pool.length) + pool.length) % pool.length;

  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 760);
  const [statYears, setStatYears] = useState(0);
  const [statFamilies, setStatFamilies] = useState(0);
  const [weekIndex, setWeekIndex] = useState(0);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [testimonialShown, setTestimonialShown] = useState(true);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<number[]>([]);
  const [quizSelected, setQuizSelected] = useState<number | null>(null);
  const [quizShown, setQuizShown] = useState(true);
  const [quizDone, setQuizDone] = useState(false);
  const [galleryFilter, setGalleryFilter] = useState('All');
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [contactSent, setContactSent] = useState(false);
  const [newsletterSent, setNewsletterSent] = useState(false);

  const [heroInView, setHeroInView] = useState(false);
  const heroRef = useRef<HTMLElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const sunRef = useRef<HTMLDivElement>(null);
  const skyRef = useRef<HTMLDivElement>(null);
  const raysRef = useRef<HTMLDivElement>(null);
  const ridgeRef = useRef<SVGPathElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const weeksActiveRef = useRef<HTMLDivElement>(null);
  const storiesFigRef = useRef<HTMLElement>(null);
  const storiesDrag = useRef({ startX: 0, startY: 0, deltaX: 0, active: false, axis: null as 'x' | 'y' | null });

  const selectedDog = content.lineage.find((dog) => dog.id === lineageId);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && closeOverlays();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeOverlays]);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', menuOpen || Boolean(lineageId));
    return () => document.body.classList.remove('no-scroll');
  }, [menuOpen, lineageId]);

  // Responsive breakpoint
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 760);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Trigger hero entrance animations when the hero scrolls into view (not on load).
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) { setHeroInView(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) { setHeroInView(true); io.disconnect(); } });
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Count-up hero stats — starts only once the hero is in view
  useEffect(() => {
    if (!heroInView) return;
    let raf = 0;
    const t0 = performance.now();
    const dur = 1300;
    const delay = 500;
    const tick = (t: number) => {
      const p = Math.min(1, Math.max(0, (t - t0 - delay) / dur));
      const e = 1 - Math.pow(1 - p, 3);
      setStatYears(Math.round(7 * e));
      setStatFamilies(Math.round(60 * e));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [heroInView]);

  // Testimonial rotation: blur out, swap, blur in — 10s after the last change
  // (manual or automatic), so a swipe/dot click doesn't get interrupted by an
  // auto-advance a moment later.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setTestimonialShown(false);
      window.setTimeout(() => {
        setTestimonialIndex((value) => (value + 1) % testimonialsData.length);
        setTestimonialShown(true);
      }, 450);
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [testimonialIndex]);

  const changeTestimonial = (index: number) => {
    if (index === testimonialIndex) return;
    setTestimonialShown(false);
    window.setTimeout(() => {
      setTestimonialIndex(index);
      setTestimonialShown(true);
    }, 450);
  };

  // Swipe/drag support for the testimonial card. Pointer Events cover touch,
  // mouse, and pen in one handler set. During the drag we mutate the figure's
  // transform directly via ref (no re-renders); on release we either snap the
  // drag back or commit to the next/previous testimonial past a distance threshold.
  const onStoriesPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (!event.isPrimary) return;
    storiesDrag.current = { startX: event.clientX, startY: event.clientY, deltaX: 0, active: true, axis: null };
    event.currentTarget.setPointerCapture(event.pointerId);
    const fig = storiesFigRef.current;
    if (fig) fig.style.transition = 'none';
  };

  const onStoriesPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = storiesDrag.current;
    if (!drag.active) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (drag.axis === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (drag.axis !== 'x') return;
    drag.deltaX = dx;
    const fig = storiesFigRef.current;
    if (fig) {
      const clamped = Math.max(-120, Math.min(120, dx));
      fig.style.transform = `translateX(${clamped}px)`;
      fig.style.opacity = String(Math.max(0.45, 1 - Math.abs(dx) / 320));
    }
  };

  const endStoriesDrag = () => {
    const drag = storiesDrag.current;
    if (!drag.active) return;
    drag.active = false;
    const fig = storiesFigRef.current;
    if (fig) {
      fig.style.transition = 'transform .32s cubic-bezier(.16,1,.3,1), opacity .32s ease';
      fig.style.transform = 'translateX(0px)';
      fig.style.opacity = '';
    }
    const threshold = 56;
    if (drag.axis === 'x' && Math.abs(drag.deltaX) > threshold) {
      const count = testimonialsData.length;
      const next = drag.deltaX < 0 ? (testimonialIndex + 1) % count : (testimonialIndex - 1 + count) % count;
      changeTestimonial(next);
    }
    drag.deltaX = 0;
    drag.axis = null;
  };

  const onStoriesKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    const count = testimonialsData.length;
    if (event.key === 'ArrowRight') changeTestimonial((testimonialIndex + 1) % count);
    else if (event.key === 'ArrowLeft') changeTestimonial((testimonialIndex - 1 + count) % count);
  };

  const onDogStripKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const strip = event.currentTarget;
    const distance = Math.max(260, Math.round(strip.clientWidth * 0.8));
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      strip.scrollBy({ left: event.key === 'ArrowRight' ? distance : -distance, behavior });
    } else if (event.key === 'Home') {
      event.preventDefault();
      strip.scrollLeft = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      strip.scrollLeft = strip.scrollWidth;
    }
  };

  // Scroll progress bar
  useEffect(() => {
    const onScroll = () => {
      const bar = progressRef.current;
      if (!bar) return;
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      bar.style.width = (max > 0 ? (h.scrollTop / max) * 100 : 0) + '%';
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lenis smooth scroll + anchor smooth-scroll (best effort, graceful if not loaded)
  useEffect(() => {
    const LenisCtor = (window as unknown as { Lenis?: new (opts: unknown) => { on: (e: string, cb: () => void) => void; raf: (t: number) => void; scrollTo: (t: Element, o: unknown) => void; destroy: () => void } }).Lenis;
    if (!LenisCtor) return;
    const lenis = new LenisCtor({ duration: 1.15, easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true, wheelMultiplier: 0.9 });
    let raf = 0;
    const loop = (time: number) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    document.documentElement.style.scrollBehavior = 'auto';
    const anchorHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const anchor = target.closest && target.closest('a[href^="#"]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (href && href.length > 1) {
        const dest = document.querySelector(href);
        if (dest) { event.preventDefault(); lenis.scrollTo(dest, { offset: -72, duration: 1.2 }); }
      }
    };
    document.addEventListener('click', anchorHandler);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('click', anchorHandler);
      document.documentElement.style.scrollBehavior = '';
      try { lenis.destroy(); } catch { /* noop */ }
    };
  }, []);

  // Pinned sunrise scene — scroll-driven on both desktop and mobile.
  // rAF-throttled, and all DOM reads are batched before writes to avoid
  // per-scroll layout thrashing (the main source of mobile stutter).
  useEffect(() => {
    const track = trackRef.current;
    const sun = sunRef.current;
    if (!track || !sun) return;
    const stages = weeksData.length;
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const sunDisc = sun.querySelector<HTMLElement>('.weeks-sun');
    let lastIdx = -1;
    let ticking = false;

    const render = () => {
      ticking = false;
      // --- READS: gather every layout measurement first ---
      const rect = track.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const p = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
      // Positions reflect the previous frame's transform (one-frame lag is
      // imperceptible) — reading here, before the write below, avoids a forced reflow.
      const sunTop = sunDisc ? sunDisc.getBoundingClientRect().top : 0;
      const ridgeTop = ridgeRef.current ? ridgeRef.current.getBoundingClientRect().top : 0;

      // --- WRITES: mutate styles together ---
      sun.style.transform = 'translate(-50%, ' + lerp(210, -150, p).toFixed(1) + 'px)';
      if (skyRef.current) skyRef.current.style.opacity = Math.min(1, p * 1.15).toFixed(3);
      if (ridgeRef.current) ridgeRef.current.style.opacity = Math.min(1, p * 1.25).toFixed(3);
      if (raysRef.current) raysRef.current.style.opacity = (Math.max(0, (p - 0.66) / 0.34) * 0.9).toFixed(3);
      if (weeksActiveRef.current && sunDisc && ridgeRef.current) {
        const sunTravel = 360;
        const horizonCrossing = Math.min(1, Math.max(0, p + (sunTop - ridgeTop) / sunTravel));
        const gotchaStart = (stages - 1) / stages;
        const glowProgress = gotchaStart > horizonCrossing
          ? Math.min(1, Math.max(0, (p - horizonCrossing) / (gotchaStart - horizonCrossing)))
          : Number(p >= gotchaStart);
        weeksActiveRef.current.style.setProperty('--gotcha-glow-progress', glowProgress.toFixed(3));
      }
      if (hintRef.current) hintRef.current.style.opacity = (1 - Math.min(1, p * 3)).toFixed(3);
      const idx = Math.min(stages - 1, Math.floor(p * stages * 0.9999));
      if (idx !== lastIdx) { lastIdx = idx; setWeekIndex(idx); }
    };

    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(render); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    render();
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); };
  }, []);

  // Highlight the chosen answer, then blur/fade to the next question (or the result).
  const answerQuiz = (optionIndex: number, points: number) => {
    if (quizSelected !== null) return;
    setQuizSelected(optionIndex);
    window.setTimeout(() => {
      setQuizShown(false);
      window.setTimeout(() => {
        setQuizAnswers((prev) => [...prev.slice(0, quizIndex), points]);
        if (quizIndex === quizData.length - 1) setQuizDone(true);
        else setQuizIndex((value) => value + 1);
        setQuizSelected(null);
        setQuizShown(true);
      }, 430);
    }, 260);
  };

  const quizBack = () => {
    if (quizIndex === 0 || quizSelected !== null) return;
    setQuizShown(false);
    window.setTimeout(() => {
      setQuizAnswers((prev) => prev.slice(0, quizIndex - 1));
      setQuizIndex((value) => value - 1);
      setQuizShown(true);
    }, 430);
  };

  const restartQuiz = () => {
    setQuizShown(false);
    window.setTimeout(() => {
      setQuizDone(false);
      setQuizIndex(0);
      setQuizAnswers([]);
      setQuizSelected(null);
      setQuizShown(true);
    }, 300);
  };

  const submitContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try { await postJson('/api/contact', Object.fromEntries(data)); } catch { /* still confirm */ }
    setContactSent(true);
  };

  const submitNewsletter = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try { await postJson('/api/newsletter', { email: data.get('email') }); } catch { /* still confirm */ }
    setNewsletterSent(true);
  };

  const quizScore = quizAnswers.reduce((sum, points) => sum + points, 0);
  const quizPct = Math.round(((quizIndex + 1) / quizData.length) * 100);
  const gallery = content.gallery.filter((item) => galleryFilter === 'All' || item.tag === galleryFilter);
  const activeIdx = Math.min(weekIndex, weeksData.length - 1);
  const activeWeek = weeksData[activeIdx];
  const current = testimonialsData[testimonialIndex];

  const litterCards = buildLitterCards(litters);
  const availabilityLitter = litterCards.find((litter) => litter.open && litter.col3Value !== '0 remaining');

  return (
    <div className="site-shell">
      <a className="skip-link" href="#dogs">Skip to content</a>

      {/* ===================== NAV ===================== */}
      <nav className="nav">
        <div ref={progressRef} className="scroll-progress" />
        <div className="nav-inner">
          <a href="#top" className="brand">sugarloaf<span>.</span></a>
          {!isMobile && (
            <div className="nav-links">
              <a href="#dogs">Our dogs</a>
              <a href="#litters">Puppies</a>
              <a href="#process">How it works</a>
              <a href="#gallery">Gallery</a>
              <a href="#faq">FAQ</a>
              <Link className="nav-apply" to="/apply">Apply →</Link>
            </div>
          )}
          {isMobile && (
            <button className={`menu-button ${menuOpen ? 'open' : ''}`} type="button" aria-label="Menu" aria-expanded={menuOpen} onClick={toggleMenu}><i /><i /><i /></button>
          )}
        </div>
      </nav>

      <button className={`drawer-backdrop ${menuOpen ? 'open' : ''}`} onClick={closeMenu} aria-label="Close menu" />
      <aside className={`drawer ${menuOpen ? 'open' : ''}`} aria-hidden={!menuOpen}>
        <div className="drawer-head"><strong>Menu</strong><button onClick={closeMenu} aria-label="Close menu">✕</button></div>
        <div className="drawer-links">
          {['dogs|Our dogs', 'litters|Puppies', 'process|How it works', 'gallery|Gallery', 'faq|FAQ'].map((item) => {
            const [id, label] = item.split('|');
            return <a key={id} onClick={closeMenu} href={`#${id}`}>{label}</a>;
          })}
        </div>
        <Link onClick={closeMenu} className="drawer-apply" to="/apply">Apply for a puppy →</Link>
      </aside>

      <main className="site-sections">

      {/* ===================== HERO ===================== */}
      <header id="top" ref={heroRef} className={`hero ${heroInView ? 'in' : ''}`} data-ed-section="hero" data-ed-name="Hero" {...sectionState('hero')}>
        <div className="paw-pattern" aria-hidden />
        <div className="hero-inner">
          <div className="hero-grid">
            <div className="hero-copy">
              <Headline text={content.hero.title} field="hero:heading" />
              <div className="hero-sub">
                <p data-ed-field="hero:subtitle">{content.hero.subtitle}</p>
                <div className="hero-buttons">
                  <a className="btn-apply" href="#litters">{content.hero.ctaPrimary}</a>
                  <a className="btn-meet" href="#dogs">{content.hero.ctaSecondary}</a>
                </div>
              </div>
            </div>
            <div className="hero-aside">
              {content.features.availabilityCard && availabilityLitter && !isMobile && (
                <a href={availabilityLitter.detailHref} className="availability-card">
                  <small><i>●</i> ACCEPTING APPLICATIONS</small>
                  <strong>Next: {availabilityLitter.pairingName}</strong>
                  <span>{availabilityLitter.col1Label.toLowerCase()} {availabilityLitter.col1Value} · {availabilityLitter.col3Value}</span>
                  <b>View this litter →</b>
                </a>
              )}
              <div className="stats-mini">
                <div><strong>{statYears} yrs</strong><span>BREEDING</span></div>
                <div><strong>{statFamilies}+</strong><span>FAMILIES</span></div>
              </div>
            </div>
          </div>

          <div className="film-window">
            <div className="film-strip">
              {[...stripData, ...stripData].map((frame, index) => {
                const slot = photoSlot(index);
                return (
                  <div
                    key={index}
                    className="film-frame site-crop-frame"
                    data-ed-image={`heroPhotos:${slot}`}
                    style={{ width: frame.w, transform: `rotate(${frame.tilt}deg) translateY(${frame.dy}px)` }}
                  >
                    <CroppedImage src={pool[slot]} alt="" crop={content.heroPhotoCrops?.[slot]} fallbackPosition="center" />
                  </div>
                );
              })}
            </div>
          </div>

          {content.features.trustBar && (
            <div className="trust-bar">
              {content.hero.trust.map((label, i) => <span key={i}>{label}</span>)}
            </div>
          )}
        </div>
      </header>

      {/* ===================== OUR DOGS ===================== */}
      <section id="dogs" className="section section-sand" data-ed-section="dogs" data-ed-name="Our dogs" {...sectionState('dogs')}>
        <div className="section-inner">
          <Reveal>
            <div className="dogs-head">
              <p className="kicker" data-ed-field="dogs:dogsKicker">{copy.dogsKicker}</p>
              <h2 style={headingStyle} data-ed-field="dogs:dogsTitle">{copy.dogsTitle}</h2>
              <p style={{ fontSize: 15, lineHeight: 1.6, color: '#6E6455', margin: 0 }}>{copy.dogsSubtext}</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div
              className="dog-strip"
              role="region"
              aria-label="Our dogs, scroll horizontally to view every profile"
              aria-describedby="dogs-scroll-hint"
              tabIndex={0}
              onKeyDown={onDogStripKeyDown}
            >
              {content.lineage.map((dog) => (
                <button className="dog-card" key={dog.id} onClick={() => openLineage(dog.id)} aria-label={`View details for ${dog.name}`} data-ed-field={`dogs:dog-${dog.id}`}>
                  <div className="dog-photo site-crop-frame" data-ed-image={`dogs:${dog.id}`}>
                    <CroppedImage src={dog.image.src} alt="" crop={dog.image.crop} fallbackPosition="center 25%" />
                  </div>
                  <div className="dog-body">
                    <small>{dog.title.toUpperCase()}</small>
                    <div className="dog-row">
                      <div><strong>{dog.name}</strong><em>{dog.registeredName}</em></div>
                      <b>→</b>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Reveal>
          <p id="dogs-scroll-hint" className="dogs-swipe">← scroll or swipe to see all dogs →</p>
        </div>
      </section>

      {/* ===================== HEALTH ===================== */}
      <section id="health" className="section" data-ed-section="health" data-ed-name="Health" {...sectionState('health')}>
        <div className="health-grid">
          <Reveal className="health-left">
            {copy.healthKicker && <p className="kicker">{copy.healthKicker}</p>}
            <h2 style={headingStyle}>{copy.healthTitle}</h2>
            <p className="intro">{copy.healthIntro}</p>
            <div className="go-home">
              <h3>Every puppy goes home with</h3>
              <div>{goHomeItems.map((item) => <span key={item}>✓ {item}</span>)}</div>
            </div>
          </Reveal>
          <Reveal delay={140} className="health-list">
            {content.healthStandards.map((item) => (
              <div className="health-item" key={item.title}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#B9895A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">{healthIconFor(item)}</svg>
                <div>
                  <div className="health-item-head"><p>{item.title}</p>{item.code && <span>{item.code}</span>}</div>
                  <p>{item.body}</p>
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ===================== FIRST 8 WEEKS ===================== */}
      <section id="weeks" className="weeks" data-ed-section="weeks" data-ed-name="First 8 weeks" {...sectionState('weeks')}>
        <div ref={trackRef} className="weeks-scroll">
          <div className="weeks-pin">
            <div className="weeks-scene" aria-hidden>
              <div className="weeks-base" />
              <div ref={skyRef} className="weeks-sky" />
              <div ref={sunRef} className="weeks-sun-wrap" style={{ transform: 'translate(-50%, 210px)' }}>
                <div ref={raysRef} className="weeks-godrays" />
                <div className="weeks-sun" />
              </div>
              <svg className="weeks-ridge-svg" viewBox="0 0 1200 300" preserveAspectRatio="xMidYMax slice">
                <path d="M0 300 L0 228 C120 226 210 218 300 201 C365 188 410 160 455 126 C500 92 548 75 600 73 C655 71 700 84 744 105 C800 132 855 156 920 174 C1010 199 1090 216 1200 222 L1200 300 Z" fill="#1B150F" />
                <path ref={ridgeRef} className="weeks-ridge" d="M0 228 C120 226 210 218 300 201 C365 188 410 160 455 126 C500 92 548 75 600 73 C655 71 700 84 744 105 C800 132 855 156 920 174 C1010 199 1090 216 1200 222" fill="none" stroke="#F0C778" strokeWidth="2.5" />
              </svg>
            </div>
            <div className="weeks-header">
              <p>{copy.weeksKicker}</p>
              <h2>{copy.weeksTitle}</h2>
            </div>
            <div ref={weeksActiveRef} className={`weeks-active ${activeIdx === weeksData.length - 1 ? 'is-gotcha' : ''}`}>
              <p className="wk">{activeWeek.week}</p>
              <h3>{activeWeek.title}</h3>
              <p className="body">{activeWeek.body}</p>
            </div>
            <div className="weeks-progress">
              <div className="weeks-dots">
                {weeksData.map((_, i) => (
                  <span key={i} style={{ width: i === activeIdx ? '26px' : '9px', background: i === activeIdx ? '#F0C778' : 'rgba(240,199,120,0.3)' }} />
                ))}
              </div>
              <div ref={hintRef} className="weeks-hint">SCROLL <i>↓</i></div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== GALLERY ===================== */}
      <section id="gallery" className="section" data-ed-section="gallery" data-ed-name="Gallery" {...sectionState('gallery')}>
        <div className="section-inner">
          <Reveal>
            <div className="gallery-head">
              <div>
                <p className="kicker">{copy.galleryKicker}</p>
                <h2 style={headingStyle}>{copy.galleryTitle}</h2>
              </div>
              <div className="gallery-chips">
                {['All', 'Puppies', 'Adults', 'Adventures'].map((filter) => (
                  <button key={filter} className={filter === galleryFilter ? 'active' : ''} onClick={() => setGalleryFilter(filter)}>{filter}</button>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="gallery-grid">
              {gallery.map((item, index) => (
                <div key={index} className="gallery-item site-crop-frame" data-ed-image={`gallery:${content.gallery.indexOf(item)}`} style={{ gridRow: `span ${item.span}` }}>
                  <CroppedImage src={item.src} alt={item.caption} crop={item.crop} fallbackPosition="center" />
                  <span>{item.caption}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== PROCESS ===================== */}
      <section id="process" className="section section-sand" data-ed-section="process" data-ed-name="How it works" {...sectionState('process')}>
        <div className="section-inner">
          <Reveal>
            <div className="section-heading centered">
              <p className="kicker">{copy.processKicker}</p>
              <h2 style={headingStyle}>{copy.processTitle}</h2>
              <p style={{ fontSize: 15, color: '#6E6455', margin: 0 }}>{copy.processSubtext}</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <ol className="process-timeline">
              {processData.map(([num, title, body]) => (
                <li className="process-step" key={num}>
                  <div className="process-node">{num}</div>
                  <div className="process-text">
                    <h3>{title}</h3>
                    <p>{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* ===================== TESTIMONIALS ===================== */}
      <section id="stories" className="section" data-ed-section="stories" data-ed-name="Testimonials" {...sectionState('stories')}>
        <div className="section-inner">
          <Reveal>
            <div className="stories-head">
              <span className="k">{copy.testimonialsKicker}</span>
              <span className="s">{copy.testimonialsTitle}</span>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <div className="stories-body">
              <p className="stories-quote-mark">“</p>
              <figure
                ref={storiesFigRef}
                className="stories-fig"
                style={{ opacity: testimonialShown ? 1 : 0, filter: testimonialShown ? 'blur(0px)' : 'blur(8px)' }}
                tabIndex={0}
                role="group"
                aria-roledescription="carousel"
                aria-label={`Testimonial ${testimonialIndex + 1} of ${testimonialsData.length}. Swipe or use the arrow keys to browse.`}
                onPointerDown={onStoriesPointerDown}
                onPointerMove={onStoriesPointerMove}
                onPointerUp={endStoriesDrag}
                onPointerCancel={endStoriesDrag}
                onKeyDown={onStoriesKeyDown}
              >
                <blockquote>{current.quote}</blockquote>
                <figcaption>{current.name} <span>· {current.location}</span></figcaption>
              </figure>
              <div className="stories-dots">
                {testimonialsData.map((_, i) => (
                  <button key={i} aria-label="Show testimonial" onClick={() => changeTestimonial(i)} style={{ width: i === testimonialIndex ? '28px' : '8px', background: i === testimonialIndex ? '#DE9445' : '#E0D5BF' }} />
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== QUIZ ===================== */}
      <section id="quiz" className="section section-sand" data-ed-section="quiz" data-ed-name="Quiz" {...sectionState('quiz')}>
        <div className="section-inner">
          <Reveal>
            <div className="quiz-head">
              <span className="k">{copy.quizKicker}</span>
              <span className="s">No email required, nothing saved</span>
            </div>
          </Reveal>
          <div className="quiz-cols">
            <Reveal delay={60} className="quiz-intro">
              <h2>{copy.quizTitle}</h2>
              <p>{copy.quizSubtext}</p>
            </Reveal>
            <Reveal delay={140} className="quiz-card">
              {quizDone ? (
                <QuizResult score={quizScore} restart={restartQuiz} />
              ) : (
                <div>
                  <div className="quiz-meta">
                    <span className="q-label">{quizIndex + 1}/{quizData.length}</span>
                    <div className="quiz-progress-wrap">
                      <div className="quiz-progress"><i style={{ width: `${quizPct}%` }} /></div>
                      <span className="quiz-pct">{quizPct}%</span>
                    </div>
                  </div>
                  <div className="quiz-anim" style={{ opacity: quizShown ? 1 : 0, filter: quizShown ? 'blur(0px)' : 'blur(8px)' }}>
                    <h3>{quizData[quizIndex].q}</h3>
                    <div className="quiz-options" role="radiogroup" aria-label={quizData[quizIndex].q}>
                      {quizData[quizIndex].options.map(([label, points], oi) => (
                        <button key={label} role="radio" aria-checked={quizSelected === oi} className={quizSelected === oi ? 'selected' : ''} onClick={() => answerQuiz(oi, points)}><i />{label}</button>
                      ))}
                    </div>
                    {quizIndex > 0 && <button type="button" className="quiz-back" onClick={quizBack}>← Back</button>}
                  </div>
                </div>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===================== LITTERS ===================== */}
      <section id="litters" className="section" data-ed-section="litters" data-ed-name="Litters" {...sectionState('litters')}>
        <div className="section-inner">
          <Reveal>
            <div className="litters-head">
              <span className="k">{copy.littersKicker}</span>
              <span className="s">{copy.littersSubtext}</span>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <div className="litter-grid">
              {litterCards.map((lit) => (
                <div className="litter-card" key={lit.id}>
                  <Link to={lit.detailHref} className="litter-photo site-crop-frame" data-ed-image={`litters:${lit.id}`}>
                    <CroppedImage src={lit.image} alt="" crop={lit.imageCrop} fallbackPosition="center 30%" />
                    <span className="litter-status"><i style={{ background: lit.statusColor, animation: lit.open ? 'pulseDot 2.2s infinite' : 'none' }} />{lit.statusLabel}</span>
                    <span className="litter-badge">{lit.badge}</span>
                    <h3>{lit.pairingName}</h3>
                  </Link>
                  <div className="litter-desc"><p>{lit.description}</p></div>
                  <div className="litter-stats">
                    <div><strong>{lit.col1Value}</strong><small>{lit.col1Label}</small></div>
                    <div><strong>{lit.col2Value}</strong><small>{lit.col2Label}</small></div>
                    <div className="accent"><strong>{lit.col3Value}</strong><small>{lit.col3Label}</small></div>
                  </div>
                  {lit.open ? (
                    <Link to="/apply" className="litter-cta open">{lit.ctaLabel} <span>→</span></Link>
                  ) : (
                    <a href="#contact" className="litter-cta closed">{lit.ctaLabel} <span>→</span></a>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section id="faq" className="section section-sand" data-ed-section="faq" data-ed-name="FAQ" {...sectionState('faq')}>
        <div className="faq-wrap">
          <Reveal>
            <div className="section-heading centered">
              <p className="kicker">{copy.faqKicker}</p>
              <h2 style={headingStyle}>{copy.faqTitle}</h2>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="faq-list">
              {content.faq.map((item, i) => (
                <div className={`faq-item ${faqOpen === i ? 'open' : ''}`} key={item.question}>
                  <button type="button" className="faq-q" onClick={() => setFaqOpen(faqOpen === i ? null : i)} aria-expanded={faqOpen === i}>
                    {item.question}<span>+</span>
                  </button>
                  <div className="faq-a"><div className="faq-a-inner"><p>{item.answer}</p></div></div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {content.facebook?.enabled && (
        <FacebookSection
          facebook={content.facebook}
          sectionState={sectionState('facebook')}
        />
      )}

      {/* ===================== CONTACT ===================== */}
      <section id="contact" className="section" data-ed-section="contact" data-ed-name="Contact" {...sectionState('contact')}>
        <div className="contact-grid">
          <Reveal className="contact-left">
            <p className="kicker">{copy.contactKicker}</p>
            <h2 style={headingStyle}>{copy.contactTitle}</h2>
            <p className="intro">{copy.contactIntro}</p>
            <a href={`mailto:${copy.contactEmail}`}>{copy.contactEmail}</a>
            <p className="ig">Instagram · {copy.contactInstagram}</p>
          </Reveal>
          <Reveal delay={140} className="contact-right">
            {contactSent ? (
              <div className="contact-success">
                <p className="ck">✓</p>
                <strong>Message sent!</strong>
                <p className="msg">We'll get back to you shortly, usually within a day.</p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={submitContact}>
                <input name="firstName" type="text" placeholder="First name" required />
                <input name="lastName" type="text" placeholder="Last name" required />
                <input className="full" name="email" type="email" placeholder="Email address" required />
                <textarea name="message" placeholder="Your message" rows={5} minLength={10} required />
                <button type="submit">Send message</button>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      </main>

      {/* ===================== FOOTER ===================== */}
      <footer className="footer" data-ed-section="footer" data-ed-name="Footer" hidden={hiddenSections.has('footer')}>
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <a href="#top" className="brand">sugarloaf<span>.</span></a>
              <p>{copy.footerBlurb}</p>
              {content.features.newsletterSignup && (newsletterSent ? (
                <p className="signed">✓ You're on the list! We'll email you when a litter is announced.</p>
              ) : (
                <form onSubmit={submitNewsletter}>
                  <input name="email" type="email" placeholder="you@example.com" required />
                  <button type="submit">Notify me</button>
                </form>
              ))}
            </div>
            <div className="footer-cols">
              <div>
                <h4>EXPLORE</h4>
                <div>
                  <a href="#dogs">Our dogs</a>
                  <a href="#weeks">First 8 weeks</a>
                  <a href="#gallery">Gallery</a>
                  <a href="#quiz">Readiness quiz</a>
                  <a href="#litters">Litters</a>
                  <a href="#faq">FAQ</a>
                </div>
              </div>
              <div>
                <h4>RESOURCES</h4>
                <div>
                  <a href="https://grca.org" target="_blank" rel="noopener noreferrer">Golden Retriever Club of America</a>
                  <a href="https://akc.org" target="_blank" rel="noopener noreferrer">American Kennel Club</a>
                  <a href="https://ofa.org" target="_blank" rel="noopener noreferrer">OFA Health Database</a>
                </div>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© {new Date().getFullYear()} Sugarloaf Golden Retrievers. All rights reserved.</span>
            <Link to="/admin">Breeder login</Link>
          </div>
        </div>
      </footer>

      {/* ===================== DOG MODAL ===================== */}
      {selectedDog && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closeOverlays()}>
          <section className="dog-modal" role="dialog" aria-modal="true" aria-label={selectedDog.name}>
            <div className="dog-modal-photo site-crop-frame" data-ed-image={`dogs:${selectedDog.id}`}>
              <CroppedImage src={selectedDog.image.src} alt={selectedDog.image.alt} crop={selectedDog.image.crop} fallbackPosition="center 20%" />
            </div>
            <div className="dog-modal-body">
              <div className="dog-modal-head">
                <div>
                  <small>{selectedDog.title.toUpperCase()}</small>
                  <strong>{selectedDog.name}</strong>
                  <em>{selectedDog.registeredName}</em>
                </div>
                <button onClick={closeOverlays} aria-label="Close">✕</button>
              </div>
              <p className="dog-modal-bio">{selectedDog.description}</p>
              <h5>HEALTH CLEARANCES</h5>
              <div className="dog-modal-tags">{selectedDog.health.map((item) => <span key={item} className="clr">✓ {item}</span>)}</div>
              <h5>TITLES &amp; AWARDS</h5>
              <div className="dog-modal-tags awards">{selectedDog.awards.map((item) => <span key={item} className="awd">🏅 {item}</span>)}</div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

const headingStyle = { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: 'clamp(32px, 4vw, 44px)', letterSpacing: '-0.03em', color: '#3A332A', margin: '0 0 14px' } as const;

function FacebookSection({ facebook, sectionState: state }: { facebook: SiteContent['facebook']; sectionState: { hidden: boolean; style: CSSProperties } }) {
  const pageUrl = normalizeFacebookUrl(facebook.pageUrl) ?? SUGARLOAF_FACEBOOK_PAGE_URL;
  const embeds = facebook.items.map((item) => ({ item, pluginUrl: facebookPluginUrl(item) })).filter((entry): entry is { item: SiteContent['facebook']['items'][number]; pluginUrl: string } => Boolean(entry.pluginUrl));

  return (
    <section id="facebook" className="facebook-section" data-ed-section="facebook" data-ed-name="Facebook videos" aria-labelledby="facebook-section-title" {...state}>
      <style>{FACEBOOK_SECTION_STYLES}</style>
      <div className="facebook-section-inner">
        <Reveal>
          <div className="facebook-section-head">
            <div><p className="kicker" data-ed-field="facebook:eyebrow">{facebook.eyebrow}</p><h2 id="facebook-section-title" data-ed-field="facebook:title">{facebook.title}</h2><p data-ed-field="facebook:intro">{facebook.intro}</p></div>
            <a href={pageUrl} target="_blank" rel="noopener noreferrer">Visit our Facebook page <span>↗</span></a>
          </div>
        </Reveal>
        {embeds.length ? (
          <div className="facebook-embed-grid">
            {embeds.map(({ item, pluginUrl }, index) => <FacebookEmbedCard key={item.id} item={item} pluginUrl={pluginUrl} delay={index * 80} />)}
          </div>
        ) : (
          <Reveal delay={100}>
            <a className="facebook-page-card" href={pageUrl} target="_blank" rel="noopener noreferrer">
              <span className="facebook-mark" aria-hidden>f</span>
              <span><small>SUGARLOAF GOLDEN RETRIEVERS</small><strong>See our latest videos and posts</strong><em>Open the official Facebook page for puppy updates and everyday moments.</em></span>
              <b aria-hidden>↗</b>
            </a>
          </Reveal>
        )}
      </div>
    </section>
  );
}

function FacebookEmbedCard({ item, pluginUrl, delay }: { item: SiteContent['facebook']['items'][number]; pluginUrl: string; delay: number }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <Reveal delay={delay}>
        <a className="facebook-page-card facebook-page-card-small" href={item.url} target="_blank" rel="noopener noreferrer">
          <span className="facebook-mark" aria-hidden>f</span>
          <span><small>{item.kind === 'video' ? 'FACEBOOK VIDEO' : 'FACEBOOK POST'}</small><strong>{item.title || 'View this update on Facebook'}</strong><em>The embedded player is unavailable here. Open the original on Facebook.</em></span>
          <b aria-hidden>↗</b>
        </a>
      </Reveal>
    );
  }
  return (
    <Reveal delay={delay}>
      <article className="facebook-embed-card">
        <div className={`facebook-embed-frame is-${item.kind}`}>
          <iframe
            src={pluginUrl}
            title={item.title || `Sugarloaf Facebook ${item.kind}`}
            loading="lazy"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            onError={() => setFailed(true)}
          />
        </div>
        <div><span>{item.kind === 'video' ? 'VIDEO' : 'POST'}</span><strong>{item.title || 'From Sugarloaf on Facebook'}</strong><a href={item.url} target="_blank" rel="noopener noreferrer">Open on Facebook ↗</a></div>
      </article>
    </Reveal>
  );
}

const FACEBOOK_SECTION_STYLES = `
  .facebook-section { padding: var(--theme-section-spacing, 64px) 24px; background: linear-gradient(145deg, #263853 0%, #15243b 100%); color: #fff; }
  .facebook-section-inner { width: min(var(--theme-content-width, 1240px), 100%); margin: 0 auto; }
  .facebook-section-head { display: flex; align-items: end; justify-content: space-between; gap: 28px; margin-bottom: 28px; }
  .facebook-section-head .kicker { color: #a9c6f1; margin: 0 0 8px; }
  .facebook-section-head h2 { margin: 0 0 8px; color: #fff; font-size: clamp(32px, 4vw, 46px); letter-spacing: -.035em; }
  .facebook-section-head p:last-child { max-width: 650px; margin: 0; color: #cbd7e8; line-height: 1.65; }
  .facebook-section-head > a { flex: 0 0 auto; padding: 11px 17px; border: 1px solid rgba(255,255,255,.22); border-radius: var(--theme-button-radius, 999px); color: #fff; font-size: 13px; font-weight: 800; }
  .facebook-section-head > a:hover { border-color: #a9c6f1; color: #fff; }
  .facebook-embed-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 390px), 1fr)); gap: 18px; }
  .facebook-embed-card { overflow: hidden; border: 1px solid rgba(255,255,255,.13); border-radius: var(--theme-radius, 20px); background: #fff; box-shadow: 0 24px 60px rgba(0,0,0,.22); color: var(--ink); }
  .facebook-embed-frame { width: 100%; background: #eef1f6; }
  .facebook-embed-frame.is-video { aspect-ratio: 16 / 9; }
  .facebook-embed-frame.is-post { min-height: 560px; }
  .facebook-embed-frame iframe { display: block; width: 100%; height: 100%; min-height: inherit; border: 0; }
  .facebook-embed-card > div:last-child { display: grid; grid-template-columns: 1fr auto; gap: 4px 16px; padding: 15px 17px; }
  .facebook-embed-card span { grid-column: 1 / -1; color: #4a68a0; font-size: 10px; font-weight: 900; letter-spacing: .12em; }
  .facebook-embed-card strong { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .facebook-embed-card a { color: #4a68a0; font-size: 12px; font-weight: 800; }
  .facebook-page-card { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 18px; min-height: 150px; padding: 24px 28px; border: 1px solid rgba(255,255,255,.18); border-radius: var(--theme-radius, 20px); background: rgba(255,255,255,.08); color: #fff; backdrop-filter: blur(8px); transition: transform .2s ease, background .2s ease; }
  .facebook-page-card:hover { transform: translateY(-2px); background: rgba(255,255,255,.12); color: #fff; }
  .facebook-page-card-small { min-height: 100%; }
  .facebook-mark { width: 62px; height: 62px; display: grid; place-items: end center; overflow: hidden; border-radius: 17px; background: #1877f2; color: #fff; font: 800 58px/51px Arial, sans-serif; }
  .facebook-page-card > span:nth-child(2) { display: grid; gap: 3px; }
  .facebook-page-card small { color: #a9c6f1; font-size: 10px; font-weight: 900; letter-spacing: .12em; }
  .facebook-page-card strong { font-size: clamp(18px, 2vw, 24px); }
  .facebook-page-card em { color: #cbd7e8; font-size: 13px; font-style: normal; }
  .facebook-page-card b { font-size: 23px; }
  @media (max-width: 700px) { .facebook-section-head { align-items: flex-start; flex-direction: column; } .facebook-page-card { grid-template-columns: auto 1fr; padding: 20px; } .facebook-page-card > b { display: none; } .facebook-mark { width: 50px; height: 50px; border-radius: 14px; font-size: 48px; line-height: 44px; } }
`;

function Headline({ text, field }: { text: string; field?: string }) {
  // Split into words, staggering a fadeUp; color trailing comma/period orange and break after the comma.
  const words = text.split(/\s+/);
  const nodes: ReactNode[] = [];
  words.forEach((word, i) => {
    const delay = `${(0.05 + i * 0.08).toFixed(2)}s`;
    const hasComma = word.endsWith(',');
    const hasPeriod = word.endsWith('.');
    const bare = hasComma || hasPeriod ? word.slice(0, -1) : word;
    nodes.push(
      <span key={i} className="word" style={{ '--d': delay } as CSSProperties}>
        {bare}{hasComma && <span className="dot">,</span>}{hasPeriod && <span className="dot">.</span>}
      </span>,
    );
    nodes.push(' ');
    if (hasComma) nodes.push(<br key={`br-${i}`} />);
  });
  return <h1 data-ed-field={field}>{nodes}</h1>;
}

function QuizResult({ score, restart }: { score: number; restart: () => void }) {
  const maxScore = quizData.length * 3;
  const pct = score / maxScore;
  const [title, body] = pct >= 0.8
    ? ["You're golden-ready.", "Active household, realistic expectations, and a plan, you're exactly the kind of family we love placing puppies with. Start your application and let's talk."]
    : pct >= 0.55
      ? ['Almost there.', "You've got the heart for it, a few logistics (time alone, budget, or training plans) are worth sorting first. Apply anyway and mention them; we're happy to help you plan."]
      : ["Maybe not yet, and that's okay.", "A golden thrives on time, exercise, and budget headroom. If those pieces aren't in place yet, waiting six months beats rehoming later. We'll be here when you're ready."];
  return (
    <div className="quiz-result">
      <p className="res">YOUR RESULT</p>
      <h3>{title}</h3>
      <p className="body">{body}</p>
      <div className="quiz-result-actions">
        <Link to="/apply">Apply for a puppy</Link>
        <button onClick={restart}>Retake</button>
      </div>
    </div>
  );
}

type LitterCard = {
  id: string; image: string; imageCrop?: ImageCrop | null; open: boolean; statusLabel: string; statusColor: string; badge: string; pairingName: string;
  description: string; col1Label: string; col1Value: string; col2Label: string; col2Value: string; col3Label: string; col3Value: string;
  ctaLabel: string; detailHref: string;
};

function buildLitterCards(litters: Litter[]): LitterCard[] {
  const defaults: LitterCard[] = [
    {
      id: 'litA', image: '/images/bento_puppies.jpg', open: true, statusLabel: 'ACCEPTING APPLICATIONS', statusColor: '#4F9960',
      badge: 'LITTER A · 2026', pairingName: 'Fame × Apollo',
      description: "An outcross pairing we've planned for two years, classic blocky heads, water-loving lines, steady family temperaments.",
      col1Label: 'BORN', col1Value: 'June 2026', col2Label: 'GO-HOME', col2Value: 'Aug 2026', col3Label: 'SPOTS', col3Value: '2 remaining',
      ctaLabel: 'Start your application', detailHref: '/litters/litA',
    },
    {
      id: 'litB', image: '/images/about.jpg', open: false, statusLabel: 'PLANNED', statusColor: '#B08540',
      badge: 'LITTER B · 2027', pairingName: 'TBD × TBD',
      description: "Our next planned pairing. Join the announcement list and you'll be the first to hear when we confirm it.",
      col1Label: 'EXPECTED', col1Value: '2027', col2Label: 'SIRE', col2Value: 'Announcing soon', col3Label: 'STATUS', col3Value: 'List open',
      ctaLabel: 'Get announcements', detailHref: '/litters/litB',
    },
  ];
  if (!litters.length) return defaults;
  const now = Date.now();
  return litters.map((lit) => {
    const open = lit.status === 'available' || lit.status === 'open';
    const expected = new Date(`${lit.expectedDate}T12:00:00`);
    const valid = !Number.isNaN(expected.getTime());
    const dateLabel = valid ? expected.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : lit.expectedDate;
    // A litter whose date has passed has been born, not "expected".
    const born = valid && expected.getTime() < now;
    return {
      id: lit.id, image: lit.imageUrl || '/images/bento_puppies.jpg', imageCrop: lit.imageCrop, open,
      statusLabel: open ? 'ACCEPTING APPLICATIONS' : lit.status.toUpperCase(),
      statusColor: open ? '#4F9960' : '#B08540',
      badge: dateLabel.toUpperCase(), pairingName: lit.title, description: lit.description,
      col1Label: born ? 'BORN' : 'EXPECTED', col1Value: dateLabel, col2Label: 'STATUS', col2Value: open ? 'Open' : lit.status,
      col3Label: 'SPOTS', col3Value: `${lit.spotsLeft} remaining`,
      ctaLabel: open ? 'Start your application' : 'Get announcements', detailHref: `/litters/${lit.id}`,
    };
  });
}
